import {
  DOCUMENT_FILE_ALLOWED_MIME_TYPES,
  DOCUMENT_FILE_ALLOWED_TYPES_LABEL,
  DOCUMENT_FILE_MAX_SIZE_BYTES,
  DocumentBulkUploadResult,
  isAllowedDocumentMimeType,
} from 'mapistry-shared';

export type { DocumentBulkUploadResult };

export const DOCUMENT_FILE_MAX_SIZE_LABEL = `${
  DOCUMENT_FILE_MAX_SIZE_BYTES / (1024 * 1024)
}MB`;

export function getDocumentUploadEmptyStateSecondaryText({
  uploadButtonLabel,
  suffix,
}: {
  uploadButtonLabel: string;
  suffix?: string;
}): string {
  const limits = `${DOCUMENT_FILE_ALLOWED_TYPES_LABEL} (up to ${DOCUMENT_FILE_MAX_SIZE_LABEL} each)`;
  const base = `Click '${uploadButtonLabel}' to upload ${limits}`;
  return suffix ? `${base}${suffix}` : `${base}.`;
}

// Extensions for the HTML `accept` attribute and as a fallback when the browser
// omits or misreports MIME (common on macOS for Office files).
const DOCUMENT_FILE_UPLOAD_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.zip',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.avif',
  '.mp4',
  '.mov',
  '.webm',
] as const;

export const DOCUMENT_FILE_INPUT_ACCEPT = [
  ...DOCUMENT_FILE_ALLOWED_MIME_TYPES,
  ...DOCUMENT_FILE_UPLOAD_EXTENSIONS,
  'video/*',
].join(',');

function hasAllowedDocumentExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return DOCUMENT_FILE_UPLOAD_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Client-side pre-check: MIME when present, extension when MIME is missing/unhelpful. */
export function isAllowedDocumentUpload(file: {
  mimeType: string;
  name: string;
}): boolean {
  if (file.mimeType && isAllowedDocumentMimeType(file.mimeType)) {
    return true;
  }
  if (!file.mimeType || file.mimeType === 'application/octet-stream') {
    return hasAllowedDocumentExtension(file.name);
  }
  return false;
}

type DocumentBulkUploadToast = {
  message: string;
  status: 'error' | 'success' | 'warning';
};

function formatUploadFailureMessages(
  failures: DocumentBulkUploadResult['failures'],
): string {
  return failures
    .map((f) => `Could not upload "${f.filename}": ${f.error}`)
    .join(' ');
}

/** One toast for upload outcomes — the toast UI only shows a single message at a time. */
export function getDocumentUploadToastStatus(
  rejectedMessages: string[],
  result: DocumentBulkUploadResult,
): DocumentBulkUploadToast | null {
  const { successes, failures } = result;
  const successCount = successes.length;
  const hasRejections = rejectedMessages.length > 0;
  const hasServerFailures = failures.length > 0;
  const failureMessages = formatUploadFailureMessages(failures);

  if (!successCount && !hasRejections && !hasServerFailures) {
    return null;
  }

  if (!successCount && (hasRejections || hasServerFailures)) {
    const message = hasRejections
      ? `${rejectedMessages.join(' ')}${
          hasServerFailures ? ` ${failureMessages}` : ''
        }`
      : failureMessages;
    return { status: 'error', message };
  }

  if (successCount && (hasRejections || hasServerFailures)) {
    const parts = [
      `${successCount} document${successCount === 1 ? '' : 's'} uploaded.`,
      ...rejectedMessages,
      ...(hasServerFailures ? [failureMessages] : []),
    ];
    return { status: 'warning', message: parts.join(' ') };
  }

  return {
    status: 'success',
    message: `${successCount} document${
      successCount === 1 ? '' : 's'
    } uploaded.`,
  };
}

export function partitionDocumentUploadFiles(files: File[]): {
  accepted: File[];
  rejectedMessages: string[];
} {
  const rejectedMessages: string[] = [];
  const accepted = files.filter((file) => {
    if (file.size > DOCUMENT_FILE_MAX_SIZE_BYTES) {
      rejectedMessages.push(
        `"${file.name}" exceeds the ${DOCUMENT_FILE_MAX_SIZE_LABEL} limit.`,
      );
      return false;
    }
    if (!isAllowedDocumentUpload({ mimeType: file.type, name: file.name })) {
      rejectedMessages.push(
        `"${file.name}" has an unsupported file type. Allowed types: ${DOCUMENT_FILE_ALLOWED_TYPES_LABEL}.`,
      );
      return false;
    }
    return true;
  });
  return { accepted, rejectedMessages };
}
